import React, { useRef, useState } from 'react'
import { ActivityIndicator, Dimensions } from 'react-native'

import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import { SH2, Stack, XStack } from '@zet/design-system'
import { BottomSheet } from '@zet/design-system/bottomSheet'
import { Checkbox } from '@zet/design-system/checkbox'
import { Controller, useForm } from 'react-hook-form'

import { useApiAction } from '../../api/useApiAction'
import PDFViewer from '../common/pdfViewer/index.web'
import Footer from '../footer/footer'

function googleDocLinkToPdfLink(link: string) {
  return `https://docs.google.com/gview?embedded=true&url=${link}`
}

export const HDFCConsent = () => {
  const { data, isLoading, stateManagePost } = useApiAction()
  const [showSpinner, setShowSpinner] = useState(true)
  const [showTnC, setShowTnC] = useState(false)
  const { height } = Dimensions.get('window')

  const consentScreenConfigKeys = data?.consentScreenConfig?.keys || {}

  const pdfUri = useRef('')

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      hdfcConsentPrivacyPolicy: false,
      hdfcConsentRequestedProducts: false,
      hdfcConsentOtherProducts: false,
    },
  })

  const onSubmit = (data) => {
    const payload = { formData: [] }
    Object.keys(data).forEach((key) => {
      payload.formData.push({
        id: consentScreenConfigKeys?.[key],
        value: data?.[key],
      })
    })
    stateManagePost({ payload })
  }

  const openBottomSheet = (_pdfUri: string) => {
    pdfUri.current = googleDocLinkToPdfLink(_pdfUri)
    setShowTnC(true)
    setShowSpinner(true)
  }

  const onDismiss = () => {
    setShowTnC(false)
    setShowSpinner(false)
  }

  const hideSpinnerLoader = () => {
    setShowSpinner(false)
  }

  return (
    <KeyBoardWrapper
      className="flex-1 bg-white"
      BottomSheet={
        <Footer
          buttonProps={{
            onPress: handleSubmit(onSubmit),
            disabled: isLoading || !isValid,
          }}
        />
      }
    >
      <Stack className="gap-4 p-4">
        <Controller
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <XStack className="items-center">
              <Checkbox
                onChange={(value) => {
                  onChange(value)
                  onBlur()
                }}
                accesibilityLabel="hdfcConsentPrivacyPolicy"
                checked={value}
              />
              <SH2 className="text-grays-60 ml-2">
                I have read, understood and hereby accept the&nbsp;
                <SH2
                  className="text-primary-100"
                  onPress={() =>
                    openBottomSheet(
                      'https://onecode-asset.s3.ap-south-1.amazonaws.com/261_PrivacyPolicy.pdf'
                    )
                  }
                >
                  customized Privacy Policy for Zet in relation to collection of
                  information for HDFC Bank.
                </SH2>
              </SH2>
            </XStack>
          )}
          name="hdfcConsentPrivacyPolicy"
        />

        <Controller
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <XStack className="items-center">
              <Checkbox
                onChange={(value) => {
                  onChange(value)
                  onBlur()
                }}
                accesibilityLabel="hdfcConsentRequestedProducts"
                checked={value}
              />
              <SH2 className="text-grays-60 ml-2">
                I/we hereby give the&nbsp;
                <SH2
                  className="text-primary-100"
                  onPress={() =>
                    openBottomSheet(
                      'https://onecode-asset.s3.ap-south-1.amazonaws.com/261_Consent_for_Requested_Products_16_11_2022.pdf'
                    )
                  }
                >
                  consent (V.1.0) in relation to Requested Products
                </SH2>
              </SH2>
            </XStack>
          )}
          name="hdfcConsentRequestedProducts"
        />

        <Controller
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <XStack className="items-center">
              <Checkbox
                onChange={(value) => {
                  onChange(value)
                  onBlur()
                }}
                accesibilityLabel="hdfcConsentOtherProducts"
                checked={value}
              />
              <SH2 className="text-grays-60 ml-2">
                I/we hereby give the&nbsp;
                <SH2
                  className="text-primary-100"
                  onPress={() =>
                    openBottomSheet(
                      'https://onecode-asset.s3.ap-south-1.amazonaws.com/261_Consent_for_Other_Products_16_11_2022.pdf'
                    )
                  }
                >
                  consent (V.2.0) in relation to Other Products (Optional).
                </SH2>
              </SH2>
            </XStack>
          )}
          name="hdfcConsentOtherProducts"
        />
      </Stack>
      <BottomSheet visible={showTnC} onDismiss={onDismiss}>
        <Stack style={{ height: height / 0.9 }}>
          {showSpinner && <ActivityIndicator />}
          <PDFViewer uri={pdfUri.current} onLoad={hideSpinnerLoader} />
        </Stack>
      </BottomSheet>
    </KeyBoardWrapper>
  )
}
